// track/index.tsx
import React, { useState, useMemo, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { FileText, Receipt } from 'lucide-react';

import { useAuth } from '../../../../hooks/AuthProvider';
import { useLocations } from '../../../../hooks/useLocations';
import { useDataFetching } from './hooks/useDataFetching';
import { useDuplicateDetection } from './hooks/useDuplicateDetection';

import { JOTLUTrackProps } from './types';
import { formatOrganizationName, parseCSVLine, parseDateString } from './utils';

import TransactionsView from './components/TransactionsView';
import ClientsView from './components/ClientsView';
import VendorsView from './components/VendorsView';
import AddTransactionModal from './components/AddTransactionModal';
import AddMethodModal from './components/AddMethodModal';
import AddClientModal from './components/AddClientModal';
import AddVendorModal from './components/AddVendorModal';
import CSVImportModal from './components/CSVImportModal';
import DuplicateReviewModal from './components/DuplicateReviewModal';
import MerchantLinkModal from './components/MerchantLinkModal';

const JOTLUTrack: React.FC<JOTLUTrackProps> = ({
  db,
  functions,
  selectedOrganizationId,
  onError,
  onSuccess,
  setLoading,
  currentView,
}) => {
  const { currentUser, userPermissions } = useAuth();
  const { getOrganizationName } = useLocations();

  const {
    transactions,
    transactionMethods,
    clients,
    vendors,
    loading: dataLoading,
    reloadData,
  } = useDataFetching(db, selectedOrganizationId, userPermissions);

  useEffect(() => {
    setLoading(dataLoading);
  }, [dataLoading, setLoading]);

  const {
    potentialDuplicates,
    nonDuplicateTransactions,
    findDuplicates,
    toggleDuplicateInclusion,
    toggleAllDuplicates,
    reset: resetDuplicates,
  } = useDuplicateDetection(transactions);

  // State for UI, modals, and filters
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${String(now.getMonth() + 1).padStart(2, '0')}_${now.getFullYear()}`;
  });
  const [selectedSourceFilter, setSelectedSourceFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [showAddMethod, setShowAddMethod] = useState(false);
  const [showAddClient, setShowAddClient] = useState(false);
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showDuplicateReview, setShowDuplicateReview] = useState(false);
  const [showMerchantLink, setShowMerchantLink] = useState(false);
  const [pendingMerchantLink, setPendingMerchantLink] = useState<{ merchant: string; vendorId: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canWrite = useMemo(() => userPermissions.some(p =>
    p === `organizations_${selectedOrganizationId}_write` || p === `organizations_${selectedOrganizationId}_admin`
  ), [userPermissions, selectedOrganizationId]);

  // Cloud function references
  const assignVendorToTransactionFunc = httpsCallable(functions, 'assignVendorToTransaction');
  const addTransactionFunc = httpsCallable(functions, 'addTransaction');
  const importTransactionsFunc = httpsCallable(functions, 'importTransactions');
  const createTransactionMethodFunc = httpsCallable(functions, 'createTransactionMethod');
  const createClientFunc = httpsCallable(functions, 'createClient');
  const createVendorFunc = httpsCallable(functions, 'createVendor');
  const updateVendorMerchantMappingsFunc = httpsCallable(functions, 'updateVendorMerchantMappings');

  const handleAssignVendor = async (transactionUid: string, vendorId: string) => {
    const transactionToUpdate = transactions.find(t => t.uid === transactionUid);
    if (!transactionToUpdate) {
        onError('Could not find the transaction to update.');
        return;
    }
    setLoading(true);
    try {
      await assignVendorToTransactionFunc({
        organizationId: selectedOrganizationId,
        transactionDate: transactionToUpdate.date,
        transactionUid: transactionUid,
        vendorId: vendorId,
      });
      onSuccess('Vendor assigned successfully.');
      await reloadData();
    } catch (error) {
      console.error("Error assigning vendor: ", error);
      onError('Failed to assign vendor.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = async (transactionData: any) => {
    setIsSubmitting(true);
    try {
      await addTransactionFunc({ organizationId: selectedOrganizationId, transaction: transactionData });
      onSuccess('Transaction added successfully');
      setShowAddTransaction(false);
      reloadData();
    } catch (error) {
      onError('Failed to add transaction');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddMethod = async (methodData: any) => {
    setIsSubmitting(true);
    try {
      await createTransactionMethodFunc({ organizationId: selectedOrganizationId, method: methodData });
      onSuccess('Source added successfully');
      setShowAddMethod(false);
      reloadData();
    } catch (error) {
      onError('Failed to add source');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddClient = async (clientData: any) => {
    setIsSubmitting(true);
    try {
      await createClientFunc({ organizationId: selectedOrganizationId, client: clientData });
      onSuccess('Client added successfully');
      setShowAddClient(false);
      reloadData();
    } catch (error) {
      onError('Failed to add client');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleAddVendor = async (vendorData: any) => {
      setIsSubmitting(true);
      try {
          await createVendorFunc({ organizationId: selectedOrganizationId, vendor: vendorData });
          onSuccess('Vendor added successfully');
          setShowAddVendor(false);
          reloadData();
      } catch (error) {
          onError('Failed to add vendor');
      } finally {
          setIsSubmitting(false);
      }
  };

  const handleCSVImport = async (file: File, methodId: string) => {
    setIsSubmitting(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, '').toLowerCase());
      
      const getColumnIndex = (possibleNames: string[]) => {
        for (const name of possibleNames) {
          const index = headers.findIndex(h => h.includes(name.toLowerCase()));
          if (index !== -1) return index;
        }
        return -1;
      };

      const dateIndex = getColumnIndex(['posting date', 'date']);
      const descriptionIndex = getColumnIndex(['description']);
      const amountIndex = getColumnIndex(['amount']);

      if (dateIndex === -1 || descriptionIndex === -1 || amountIndex === -1) {
        onError('CSV must contain Date, Description, and Amount columns');
        setIsSubmitting(false);
        return;
      }
      
      const parsedTxs = [];
      for(let i = 1; i < lines.length; i++) {
        const values = parseCSVLine(lines[i]);
        if (values.length < Math.max(dateIndex, descriptionIndex, amountIndex) + 1) {
          console.warn(`Row ${i + 1}: Insufficient columns, skipping`);
          continue;
        }

        const dateStr = values[dateIndex];
        const description = values[descriptionIndex];
        const amountStr = values[amountIndex];

        const transactionDate = parseDateString(dateStr);
        if (!transactionDate) {
            console.warn(`Row ${i + 2}: Invalid or unparseable date format "${dateStr}", skipping`);
            continue;
        }
        const parsedDate = transactionDate.toISOString().split('T')[0];
        
        const amount = parseFloat(amountStr.replace(/[$£€,]/g, ''));
        if (isNaN(amount)) {
            console.warn(`Row ${i + 2}: Invalid amount "${amountStr}", skipping`);
            continue;
        }

        parsedTxs.push({
            date: parsedDate,
            description,
            amount,
            type: amount < 0 ? 'debit' : 'credit',
            transactionMethodId: methodId,
            rowNumber: i + 1
        });
      }

      if (parsedTxs.length === 0) {
        onError('No valid transactions found in CSV file.');
        setIsSubmitting(false);
        return;
      }

      const { duplicates } = await findDuplicates(parsedTxs);

      if (duplicates && duplicates.length > 0) {
        setShowDuplicateReview(true);
      } else {
        await proceedWithImport(parsedTxs);
      }
    } catch (error) {
        console.error("CSV Import Error: ", error);
        onError('Failed to process CSV file.');
    } finally {
        setIsSubmitting(false);
    }
  };

  const proceedWithImport = async (transactionsToImport: any[]) => {
    setIsSubmitting(true);
    try {
      const result: any = await importTransactionsFunc({
        organizationId: selectedOrganizationId,
        transactions: transactionsToImport,
      });
      onSuccess(`Successfully imported ${result.data.totalImported} transactions.`);
      setShowCSVImport(false);
      setShowDuplicateReview(false);
      resetDuplicates();
      await reloadData();
    } catch (error) {
      onError('Failed to import transactions');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicateReviewConfirm = async () => {
    const finalTransactions = [
      ...nonDuplicateTransactions,
      ...potentialDuplicates.filter(d => d.include).map(d => d.newTransaction),
    ];
    if (finalTransactions.length > 0) {
      await proceedWithImport(finalTransactions);
    } else {
      onSuccess('No new transactions were imported.');
      setShowDuplicateReview(false);
    }
  };

  const handleMonthChange = (newMonth: string) => {
      setSelectedMonth(newMonth);
      setCurrentPage(1);
  }
  
  const handleSourceFilterChange = (sourceId: string) => {
    setSelectedSourceFilter(sourceId);
    setCurrentPage(1);
  }

  return (
    <div className="applist-main-content" style={{ overflowY: "scroll" }}>
      <div className="applist-content-header">
        <h1 className="applist-page-title">
          {currentView.charAt(0).toUpperCase() + currentView.slice(1)} - {formatOrganizationName(getOrganizationName(selectedOrganizationId) || '')}
        </h1>
      </div>
      <div className="applist-content-body">
        {currentView === 'transactions' && (
          <TransactionsView
            transactions={transactions}
            clients={clients}
            vendors={vendors}
            transactionMethods={transactionMethods}
            canWrite={canWrite}
            onAddTransaction={() => setShowAddTransaction(true)}
            onAddMethod={() => setShowAddMethod(true)}
            onImportCSV={() => setShowCSVImport(true)}
            onAssignVendor={handleAssignVendor}
            selectedMonth={selectedMonth}
            onMonthChange={handleMonthChange}
            selectedSourceFilter={selectedSourceFilter}
            onSourceFilterChange={handleSourceFilterChange}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        )}
        {currentView === 'clients' && (
          <ClientsView clients={clients} canWrite={canWrite} onAddClient={() => setShowAddClient(true)} />
        )}
        {currentView === 'vendors' && (
          <VendorsView vendors={vendors} canWrite={canWrite} onAddVendor={() => setShowAddVendor(true)} />
        )}
        {currentView === 'invoices' && (
          <div className="text-center py-12"><FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" /><h3>Invoices</h3><p>Coming Soon</p></div>
        )}
        {currentView === 'expenses' && (
            <div className="text-center py-12"><Receipt className="h-12 w-12 text-gray-400 mx-auto mb-4" /><h3>Expenses</h3><p>Coming Soon</p></div>
        )}
      </div>

      <AddTransactionModal
        isOpen={showAddTransaction}
        onClose={() => setShowAddTransaction(false)}
        onAddTransaction={handleAddTransaction}
        isLoading={isSubmitting}
        clients={clients}
        vendors={vendors}
        transactionMethods={transactionMethods}
      />
      <AddMethodModal
        isOpen={showAddMethod}
        onClose={() => setShowAddMethod(false)}
        onAddMethod={handleAddMethod}
        isLoading={isSubmitting}
      />
      <AddClientModal
        isOpen={showAddClient}
        onClose={() => setShowAddClient(false)}
        onAddClient={handleAddClient}
        isLoading={isSubmitting}
      />
       <AddVendorModal
        isOpen={showAddVendor}
        onClose={() => setShowAddVendor(false)}
        onAddVendor={handleAddVendor}
        isLoading={isSubmitting}
      />
      <CSVImportModal
        isOpen={showCSVImport}
        onClose={() => setShowCSVImport(false)}
        onImport={handleCSVImport}
        isLoading={isSubmitting}
        transactionMethods={transactionMethods}
        onCreateMethod={() => setShowAddMethod(true)}
      />
      <DuplicateReviewModal
        isOpen={showDuplicateReview}
        onClose={() => setShowDuplicateReview(false)}
        potentialDuplicates={potentialDuplicates}
        nonDuplicateTransactionsCount={nonDuplicateTransactions.length}
        onConfirm={handleDuplicateReviewConfirm}
        onToggleInclusion={toggleDuplicateInclusion}
        onToggleAll={toggleAllDuplicates}
        isLoading={isSubmitting}
      />
       {pendingMerchantLink && (
        <MerchantLinkModal
            isOpen={showMerchantLink}
            onClose={() => setShowMerchantLink(false)}
            onConfirm={() => {
                // Handle confirm logic here
                setShowMerchantLink(false);
            }}
            merchantName={pendingMerchantLink.merchant}
        />
       )}
    </div>
  );
};

export default JOTLUTrack;